import { cn } from "@tcg/ui";
import { Info } from "lucide-react";
import { useTranslation } from "react-i18next";

import { formatYen } from "@/lib/money";

import type { PaymentMethods, TenderPreview } from "@/lib/types";

import type { TenderPart } from "./useTender";

/** Brands payment-service knows (CardBrand) — shown when the terminal lists none of its own. */
const DEFAULT_BRANDS = [
  "VISA",
  "MASTER",
  "JCB",
  "AMEX",
  "DINERS",
  "PAYPAY_VIA_EDC",
  "SUICA_TRANSIT",
];

/**
 * カード (EDC) — the shop's own card terminal, not connected (design/05 §3.2, R6): choose the
 * terminal and the brand, take the payment on the terminal, then type the 承認番号 (and the last
 * 4 digits if you like). The fee (「手数料 3.24% = ¥437 → 店負担」) is shown to managers only.
 * PayPay / 交通系 taken on the same terminal are brands of the terminal (design/05 §1).
 */
export function EdcForm({
  part,
  index,
  methods,
  preview,
  onChange,
}: {
  part: TenderPart;
  index: number;
  methods: PaymentMethods | undefined;
  preview: TenderPreview | undefined;
  onChange: (changes: Partial<TenderPart>) => void;
}) {
  const { t, i18n } = useTranslation();
  const terminals = (methods?.data ?? []).filter((m) => m.method === "edc_external" && m.terminal);
  const current = terminals.find((m) => m.terminal?.id === part.terminalId) ?? terminals[0];
  const listed = current?.brands ?? [];
  const brands = listed.length > 0 ? listed.map((b) => b.brand) : DEFAULT_BRANDS;
  const line = preview?.tenders.find((l) => l.index === index);
  const showFee =
    Boolean(preview?.feesVisible) && typeof line?.fee === "number" && Boolean(line?.feePercent);
  return (
    <div className="flex flex-col gap-4">
      {terminals.length > 1 ? (
        <fieldset>
          <legend className="mb-1 font-medium">{t("tender.terminal")}</legend>
          <div className="flex flex-wrap gap-2">
            {terminals.map((m) => (
              <button
                key={m.code}
                type="button"
                aria-pressed={m.terminal?.id === part.terminalId}
                onClick={() => onChange({ terminalId: m.terminal?.id ?? "", cardBrand: "" })}
                className="min-h-pos-touch rounded-md border px-5 text-lg aria-pressed:border-primary aria-pressed:bg-accent"
              >
                {m.terminal?.label ?? m.terminal?.id}
              </button>
            ))}
          </div>
        </fieldset>
      ) : null}
      <fieldset>
        <legend className="mb-1 font-medium">{t("tender.brand")}</legend>
        <div className="grid grid-cols-3 gap-2 lg:grid-cols-6">
          {brands
            .map((brand) => ({ key: brand, label: t(`brand.${brand}`, { defaultValue: brand }) }))
            .map((b) => (
              <button
                key={b.key}
                type="button"
                aria-pressed={part.cardBrand === b.key}
                onClick={() => onChange({ cardBrand: b.key })}
                className={cn(
                  "min-h-pos-touch rounded-md border px-3 text-lg font-semibold aria-pressed:border-primary aria-pressed:bg-primary aria-pressed:text-primary-foreground",
                )}
              >
                {b.label}
              </button>
            ))}
        </div>
      </fieldset>
      {showFee && line ? (
        <p
          data-testid="edc-fee"
          className="rounded-md border bg-muted px-3 py-2 text-base tabular-nums"
        >
          {t("tender.fee_line", {
            percent: line.feePercent,
            fee: formatYen(line.fee ?? 0, i18n.resolvedLanguage ?? "ja"),
            bearer: t(`bearer.${line.feeBearer ?? "shop"}`, { defaultValue: line.feeBearer ?? "" }),
          })}
        </p>
      ) : null}
      <p className="flex items-center gap-2 rounded-md border border-status-pending bg-status-pending/15 px-3 py-2">
        <Info aria-hidden className="size-5 shrink-0" />
        {t("tender.card_hint")}
      </p>
      <div className="grid grid-cols-2 gap-3">
        <div className="flex flex-col gap-1">
          <label htmlFor={`approval-${part.id}`} className="font-medium">
            {t("tender.approval_code")}
          </label>
          <input
            id={`approval-${part.id}`}
            value={part.approvalCode}
            onChange={(e) =>
              onChange({ approvalCode: e.target.value.replace(/[^0-9A-Za-z]/g, "").slice(0, 20) })
            }
            inputMode="numeric"
            autoComplete="off"
            required
            aria-describedby={`approval-help-${part.id}`}
            className="min-h-pos-touch rounded-md border px-3 font-mono text-2xl tracking-widest"
          />
          <span id={`approval-help-${part.id}`} className="text-sm text-muted-foreground">
            {t("tender.approval_code_help")}
          </span>
        </div>
        <label className="flex flex-col gap-1">
          <span className="font-medium">{t("tender.last4")}</span>
          <input
            value={part.last4}
            onChange={(e) => onChange({ last4: e.target.value.replace(/\D/g, "").slice(0, 4) })}
            inputMode="numeric"
            autoComplete="off"
            className="min-h-pos-touch rounded-md border px-3 font-mono text-2xl tracking-widest"
          />
        </label>
      </div>
    </div>
  );
}
